import DesignTokens from '@tableau/design-tokens';
import React from 'react';
import tabbable from 'tabbable';
import { cssRule } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { CommonStyles } from '~src/commonInfra/styles/CommonStyles';
import { imgPath } from '~src/commonInfra/utils/ImgUrl';
import { applyColor }from '~src/style/ColorService';

import { Messages } from '../../../codegen/Localize';
import { cssClass } from '../../../style';

const cssPrefix = 'SkipBar';

const SkipBarSpacing = '48px';

const SkipBarContainerCSSProperties: NestedCSSProperties = {
  height: 'auto',
  maxHeight: 0,
  lineHeight: SkipBarSpacing,
  paddingLeft: SkipBarSpacing,
  paddingRight: SkipBarSpacing,
  backgroundColor: applyColor(DesignTokens.color.link.inline.default.default, '#1a699e'),
  transition: 'max-height .1s ease-out',
  verticalAlign: 'middle',
  overflow: 'hidden'
};

const SkipBarContainerStyle = cssClass(cssPrefix, 'container', SkipBarContainerCSSProperties);

const SkipBarContainerOpenedStyle = cssClass(cssPrefix, 'containerOpened', {
  ...SkipBarContainerCSSProperties,
  maxHeight: SkipBarSpacing,
});

const SkipBarLinkCSSProperties: NestedCSSProperties = {
  fontWeight: 'bold',
  color: applyColor(DesignTokens.color.font.inverse.default, '#ffffff'),
  fill: applyColor(DesignTokens.color.font.inverse.default, '#ffffff'),
  fontSize: '16px',
  textDecoration: 'none',
  padding: '4px',
  verticalAlign: 'middle',
};

const SkipBarLinkStyle = cssClass(cssPrefix, 'link', {
  ...SkipBarLinkCSSProperties,
  $nest: {
    '&:focus': {
      ...CommonStyles.Focus.OutlineIndicator,
      ...SkipBarLinkCSSProperties,
    },
    '&.hover': {
      ...SkipBarLinkCSSProperties,
    },
    '&.active': {
      ...SkipBarLinkCSSProperties,
    },
    '&.visited': {
      ...SkipBarLinkCSSProperties,
    },
  },
});

const SkipBarArrowStyle = cssClass(cssPrefix, 'arrow', {
  marginLeft: '8px',
  verticalAlign: 'middle',
});

// Marks the heading that "Skip to content" focuses. We tag it with a data attribute rather than
// a className because the heading's className is React-controlled (see SpaceHeaderContent /
// SpaceHeader); a later re-render would reconcile className back to its prop value and strip an
// injected class. A data attribute React does not own survives reconciliation (as does tabindex).
const SkipBarHeadingFocusAttr = 'data-skipbar-focus';

// Scope the focus ring to hug the heading text (width: fit-content) rather than spanning the
// full-width block-level <h1>, and only while the heading is focused so there is no layout change
// at rest. The outline is drawn just inside the padded box (small negative offset) so it is never
// clipped by an ancestor with overflow: hidden (e.g. the Home page's scrollable content section);
// the padding gives the inset ring room to draw without overlapping the heading text.
cssRule(`[${SkipBarHeadingFocusAttr}]:focus`, {
  ...CommonStyles.Focus.OutlineIndicator,
  outlineOffset: `-${CommonStyles.Focus.IndicatorWidth}px`,
  padding: '4px',
  width: 'fit-content',
});

export interface SkipBarProps {
  readonly iframeSelector?: string;
  readonly contentSelector: string;
  readonly useBackgroundColorForNonVizViewerUI?: boolean;
}

export interface SkipBarState {
  readonly opened: boolean;
}

export class SkipBar extends React.Component<SkipBarProps, SkipBarState> {
  constructor(props: SkipBarProps) {
    super(props);
    this.state = {
      opened: false,
    };
  }

  render() {
    // TFS942019 don't need alt text on icon if it repeats the text
    /* eslint-disable jsx-a11y/anchor-is-valid */
    return (
      <div className={this.state.opened ? SkipBarContainerOpenedStyle : SkipBarContainerStyle}>
        <a
          className={SkipBarLinkStyle}
          style={{ backgroundColor: this.props.useBackgroundColorForNonVizViewerUI? applyColor(DesignTokens.color.component.background.cta.default.default, CommonStyles.Colors.HyperlinkBlueHover): null }}
          href="javascript:void(0)"
          onClick={this.onClick}
          onFocus={this.onFocus}
          onBlur={this.onBlur}
        >
          {Messages.skipToContent()}
          <img className={SkipBarArrowStyle} alt={''} src={imgPath('skip_to_content_arrow.svg')} />
        </a>
      </div>
    );
  }

  onClick = () => {
    if (this.props.iframeSelector) {
      const iframeSelectorVar = $(this.props.iframeSelector);
      const vizIsVisible = iframeSelectorVar.length > 0;

      if (vizIsVisible) {
        const dashboardRegion = iframeSelectorVar
          .focus()
          .contents()
          .find('#tab-dashboard-region')[0];
        dashboardRegion && tabbable(dashboardRegion)[0]?.focus();

        // focus on error element if exists
        iframeSelectorVar
          .contents()
          .find(this.props.contentSelector)
          .focus();
        return;
      }

      $(this.props.contentSelector).focus();
    } else {
      // WCAG SC 2.4.3 Focus Order: move focus to the top of the content rather than skipping
      // straight to the first focusable control, so screen reader users don't miss content that
      // precedes it. Prefer the page's main heading (the first <h1>): focusing it makes screen
      // readers announce just "<page title>, heading" and lands the user at the true top of the
      // page. Headings aren't focusable by default, so make it programmatically focusable without
      // affecting tab order.
      const contentAreaElement = document.body.querySelector(this.props.contentSelector) as HTMLElement;
      if (contentAreaElement) {
        const headingElement = contentAreaElement.querySelector('h1') as HTMLElement;
        if (headingElement) {
          if (!headingElement.hasAttribute('tabindex')) {
            headingElement.setAttribute('tabindex', '-1');
          }
          // Scope the focus ring to the heading text rather than the full-width block-level <h1>.
          headingElement.setAttribute(SkipBarHeadingFocusAttr, '');
          headingElement.focus();
        }
        // If there is no <h1>, or focusing it didn't take (e.g. the heading is present in the DOM
        // but hidden, so .focus() is a no-op), fall back to the first enabled focusable descendant.
        // Do NOT focus the container itself — focusing [role=main] makes screen readers read the
        // entire landmark subtree aloud.
        if (document.activeElement !== headingElement) {
          this.focusFirstFocusableDescendant(contentAreaElement);
        }
      }
    }
  };

  private focusFirstFocusableDescendant = (contentAreaElement: HTMLElement) => {
    const focusableElements = contentAreaElement.querySelectorAll('button, a, input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])');
    for (let i = 0; i < focusableElements.length; i++) {
      const currentElement = focusableElements[i] as HTMLElement;
      if (!(currentElement as any).disabled) {
        currentElement.focus();
        break;
      }
    }
  };

  onFocus = () => {
    this.setState({ opened: true });
  };

  onBlur = () => {
    this.setState({ opened: false });
  };
}
